import { entityEffects, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { routerActions } from '@opengov/compose-app-router/state';
import { createSelector } from '@opengov/compose-app/reselect';
import { IEntity } from '@opengov/og-api-ts';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { OrganizeStoriesAndFolders } from '../components';
import { fetchCurrentSubjectClaims } from '../api/subjectClaimsApi';
import { selectOrganizeStoriesAndFoldersProps } from '../selectors/organizeStoriesAndFoldersSelectors';
import { getSession } from '@opengov/app-core/session/selectors';
import { hasPrivilege } from '../utils';
import { STORIES_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import { ISubjectClaims } from '../../types';

export const organizeStoriesAndFoldersRoute = {
  name: 'OrganizeStoriesAndFolders',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Story', name)),
  selector: selectOrganizeStoriesAndFoldersProps,
  component: OrganizeStoriesAndFolders,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation, onSuccess, onError) => {
      const { entitySubdomain, folderId: _folderId } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: IEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }

          const currentSubjectClaims = await fetchCurrentSubjectClaims({
            userUuid: getSession(state).uuid,
            entityUuid: entity.uuid
          });

          const isAdmin = hasPrivilege(currentSubjectClaims as ISubjectClaims, STORIES_ADMIN_PRIVILEGE);

          if (!isAdmin) {
            return onError(
              'User does not have permission...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }

          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};
